import React, { useLayoutEffect, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ButtonIconOnly from "components/ButtonIconOnly";

const Popout = ({ 
  isOpen, 
  onClose, 
  anchorRef, 
  positioning = "bottom",
  header = true,
  title = "Popout Title", 
  children 
}) => {
  const popoutRef = useRef(null);
  const [position, setPosition] = useState(null); // Start as null to prevent flicker

  useLayoutEffect(() => {
    if (isOpen && anchorRef?.current && popoutRef.current) {
      const anchorRect = anchorRef.current.getBoundingClientRect();
      const popoutWidth = popoutRef.current?.offsetWidth || 0;
      const popoutHeight = popoutRef.current?.offsetHeight || 0;
      const spacing = 8; // Space between the button and popout
      let top, left;

      switch (positioning) {
        case "top":
          top = anchorRect.top - popoutHeight - spacing + window.scrollY;
          left = anchorRect.left + (anchorRect.width / 2) - (popoutWidth / 2);
          break;
        case "top-left":
          top = anchorRect.top - popoutHeight - spacing + window.scrollY;
          left = anchorRect.right - popoutWidth;
          break;
        case "top-right":
          top = anchorRect.top - popoutHeight - spacing + window.scrollY;
          left = anchorRect.right - popoutWidth;
          break;
        case "bottom":
          top = anchorRect.bottom + spacing + window.scrollY;
          left = anchorRect.left + (anchorRect.width / 2) - (popoutWidth / 2);
          break;
        case "bottom-left":
          top = anchorRect.bottom + spacing + window.scrollY;
          left = anchorRect.right - popoutWidth;
          break;
        case "bottom-right":
          top = anchorRect.bottom + spacing + window.scrollY;
          left = anchorRect.right - popoutWidth;
          break;
        case "left":
          top = anchorRect.top + (anchorRect.height / 2) - (popoutHeight / 2) + window.scrollY;
          left = anchorRect.left - popoutWidth - spacing;
          break;
        case "right":
          top = anchorRect.top + (anchorRect.height / 2) - (popoutHeight / 2) + window.scrollY;
          left = anchorRect.right + spacing;
          break;
        default:
          top = anchorRect.bottom + spacing + window.scrollY;
          left = anchorRect.left + (anchorRect.width / 2) - (popoutWidth / 2);
      }

      // Clamp to viewport to prevent overflow
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      if (left + popoutWidth > viewportWidth) {
        left = viewportWidth - popoutWidth - 8;
      }
      if (left < 0) {
        left = 8;
      }

      if (top + popoutHeight > viewportHeight) {
        top = viewportHeight - popoutHeight - 8;
      }
      if (top < 0) {
        top = 8;
      }

      setPosition({ top, left });
    };
  }, [isOpen, anchorRef, positioning, children]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isInAnyPopout = event.target.closest(".popout-root");
      if (!isInAnyPopout && !anchorRef?.current.contains(event.target)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div 
      ref={popoutRef} 
      className="popout-root absolute z-[1050] bg-[var(--color-white)] shadow-lg rounded-lg"
      style={{
        position: "absolute",
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        visibility: position ? "visible" : "hidden",
      }}
    >
      {/* Popout Header */}
      {header && <div className="flex justify-between items-center p-6">
        <h4 className="tw-heading-4">{title}</h4>
        <ButtonIconOnly
          id={`${title}-modal-close-button`}
          data-cy={`${title}-modal-close-button`}
          icon="close"
          size={20}
          type="alt"
          onClick={onClose}
        />
      </div>}

      {/* Dynamic Content */}
      <div className="pb-6 px-6">
        {children}
      </div>
    </div>,
    document.body
  );
};

export default Popout;
